import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { formatFinanceMoney } from "@/lib/financeFormat";
import { useFinanceInvoicePaths } from "@/lib/financeRouteContext";
import type { FinanceInvoice } from "@/lib/financeTypes";
import { FinanceErrorBanner, FinanceStatusBadge } from "@/lib/financeUi";
import { Plus } from "lucide-react";

type InvoicesResponse = {
  invoices?: FinanceInvoice[];
  total?: number;
};

export function FinanceInvoicesPage() {
  const { salesInvoices, newPath, detailPath, accountsPath, paymentsPath } = useFinanceInvoicePaths();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<FinanceInvoice[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<InvoicesResponse>("/api/finance/invoices?limit=200");
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load invoices.");
        setInvoices([]);
      } else {
        setError(null);
        setInvoices(res.data?.invoices ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Invoices</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {salesInvoices
              ? "Sales invoices for this organization."
              : "Invoice list (legacy path). Prefer Sales → Invoices in the sidebar."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {salesInvoices ? (
            <>
              <Link
                to={accountsPath}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
              >
                Accounts
              </Link>
              <Link
                to={paymentsPath}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
              >
                Payments received
              </Link>
            </>
          ) : null}
          <Link
            to={newPath}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            <Plus className="h-4 w-4" aria-hidden />
            New invoice
          </Link>
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading invoices…</p> : null}
      {error ? <FinanceErrorBanner message={error} /> : null}

      {!loading && !error ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 font-semibold">Number</th>
                <th className="px-4 py-3 font-semibold">{salesInvoices ? "Account" : "Customer"}</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Issue</th>
                <th className="px-4 py-3 font-semibold">Due</th>
                {salesInvoices ? <th className="px-4 py-3 text-right font-semibold">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={salesInvoices ? 7 : 6} className="p-0">
                    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
                      <p className="font-medium text-slate-900 dark:text-white">No invoices yet</p>
                      <p className="max-w-md text-sm text-slate-600 dark:text-slate-400">
                        Create an account if you need one, then issue your first invoice.
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <Link
                          to={accountsPath}
                          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                        >
                          Add an account
                        </Link>
                        <Link
                          to={newPath}
                          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
                        >
                          Create your first invoice
                        </Link>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 dark:border-white/5">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      {inv.id ? (
                        <Link
                          to={detailPath(inv.id)}
                          className="text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                          {inv.number || inv.id}
                        </Link>
                      ) : (
                        inv.number || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.customerName?.trim() || "—"}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-600 dark:text-slate-300">
                      {typeof inv.amount === "number" ? formatFinanceMoney(inv.amount, inv.currency || "USD") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <FinanceStatusBadge status={inv.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.issueDate || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.dueDate || "—"}</td>
                    {salesInvoices ? (
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={detailPath(inv.id)}
                          className="rounded-md px-2 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                        >
                          View
                        </Link>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
